import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ENABLE_PERMISSION_CHECK } from '../config/featureFlags';
import { useAccessControl } from '../contexts/AccessControlContext';
import { resolveLandingPath, UNAUTHORIZED_LANDING_PATH } from './landingPage';
import { resolveRouteAccessRequirement, shouldSkipPostAuthRouteValidation } from './routeAccessResolver';
import { resolveRoleSwitchRedirect } from './roleSwitchRedirect';
import { wasRoleJustSwitched } from './roleSwitchFlag';
import type { AccessRequirement } from './types';

export function RequireAccess({ requirement, children }: { requirement?: AccessRequirement; children: ReactNode }) {
  const ac = useAccessControl();
  const location = useLocation();

  if (!ENABLE_PERMISSION_CHECK) return <>{children}</>;

  if (ac.loading) {
    return <div style={{ padding: 16 }}>Loading…</div>;
  }

  const allowed = ac.canAccess(requirement);

  if (!allowed) {
    // Ticket 0002137: a role switch that just invalidated this screen should quietly
    // land the user on their new home page, session intact — never /unauthorized.
    if (wasRoleJustSwitched()) {
      const target = resolveLandingPath({ roleCode: ac.roleCode, permissions: ac.permissions });
      if (target !== UNAUTHORIZED_LANDING_PATH) {
        return <Navigate to={target} replace />;
      }
      // No screen at all for this role even after switching — nothing else to do.
    }
    // State Attorney's "Tasks & Cases" list was replaced by "Attending Tasks & Cases" —
    // send them there instead of Unauthorized when a stale redirect lands them on /tasks.
    if (ac.roleCode === 'STATE_ATTORNEY' && location.pathname === '/tasks') {
      return <Navigate to="/tasks/attending" replace />;
    }
    // Direct navigation / deep link to a page this role never had access to.
    return <Navigate to="/unauthorized" replace state={{ redirectTo: location.pathname + location.search }} />;
  }

  const roleSwitchTarget = resolveRoleSwitchRedirect(location.pathname, {
    roleCode: ac.roleCode,
    permissions: ac.permissions,
  });
  if (roleSwitchTarget) {
    return <Navigate to={roleSwitchTarget} replace />;
  }

  return <>{children}</>;
}

/**
 * After login (or any RBAC refresh), redirect stale deep links — e.g. a preserved
 * create-case-request wizard URL — to the user's permission-based landing page.
 */
export function PostAuthRouteValidator() {
  const ac = useAccessControl();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!ENABLE_PERMISSION_CHECK) return;
    if (ac.loading || ac.status !== 'ready') return;
    if (shouldSkipPostAuthRouteValidation(location.pathname)) return;

    const roleSwitchTarget = resolveRoleSwitchRedirect(location.pathname, {
      roleCode: ac.roleCode,
      permissions: ac.permissions,
    });
    if (roleSwitchTarget) {
      const current = `${location.pathname}${location.search}`;
      if (roleSwitchTarget !== current && roleSwitchTarget !== location.pathname) {
        navigate(roleSwitchTarget, { replace: true });
      }
      return;
    }

    const requirement = resolveRouteAccessRequirement(location.pathname);
    if (ac.canAccess(requirement)) {
      if (ac.roleCode === 'STATE_ATTORNEY' && location.pathname.replace(/\/+$/, '') === '/tasks') {
        navigate('/tasks/attending', { replace: true });
      }
      return;
    }

    const target = resolveLandingPath({
      roleCode: ac.roleCode,
      permissions: ac.permissions,
    });
    const current = `${location.pathname}${location.search}`;
    if (target !== current && target !== location.pathname) {
      navigate(target, { replace: true });
    }
  }, [
    ac.canAccess,
    ac.loading,
    ac.permissions,
    ac.roleCode,
    ac.status,
    location.pathname,
    location.search,
    navigate,
  ]);

  return null;
}

export function PermissionGate({ requirement, children, fallback = null }: { requirement?: AccessRequirement; children: ReactNode; fallback?: ReactNode }) {
  const ac = useAccessControl();
  if (!ENABLE_PERMISSION_CHECK) return <>{children}</>;
  if (ac.loading) return <>{fallback}</>;
  return ac.canAccess(requirement) ? <>{children}</> : <>{fallback}</>;
}

